import { useEffect } from 'react';
import { useI18n } from '@/lib/i18n';

export interface OrganizationSchema {
  type: 'Organization';
  name: string;
  url: string;
  logo?: string;
  description?: string;
  foundingDate?: string;
  areaServed?: string[];
  address?: {
    locality: string;
    region: string;
    country: string;
  };
  contactPoint?: {
    telephone: string;
    contactType: string;
  };
}

export interface LocalBusinessSchema {
  type: 'LocalBusiness';
  name: string;
  description: string;
  areaServed: {
    name: string;
    containedInPlace: string;
  };
  priceRange?: string;
  telephone?: string;
  address?: {
    streetAddress?: string;
    addressLocality: string;
    addressRegion: string;
    postalCode?: string;
    addressCountry: string;
  };
}

export interface ServiceSchema {
  type: 'Service';
  name: string;
  description: string;
  provider: string;
  providerId?: string;
  url?: string;
  areaServed?: string[];
  serviceType?: string;
}

export interface BreadcrumbSchema {
  type: 'BreadcrumbList';
  items: Array<{
    name: string;
    url: string;
  }>;
}

export interface FAQSchema {
  type: 'FAQPage';
  questions: Array<{
    question: string;
    answer: string;
  }>;
}

export type SchemaData = OrganizationSchema | LocalBusinessSchema | ServiceSchema | BreadcrumbSchema | FAQSchema;

interface SchemaMarkupProps {
  schemas: SchemaData[];
}

/**
 * Component to inject JSON-LD structured data into the page head
 */
export function SchemaMarkup({ schemas }: SchemaMarkupProps) {
  const { region, config } = useI18n();
  
  useEffect(() => {
    const scriptId = 'schema-markup-jsonld';
    
    // Remove existing schema script if present
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }
    
    if (schemas.length === 0) return;
    
    const baseUrl = region === 'US' ? 'https://mtligroup.com' : 'https://mtli.ca';
    
    const jsonLdData = schemas.map(schema => buildSchema(schema, baseUrl, config));
    
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLdData.length === 1 ? jsonLdData[0] : jsonLdData);
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [schemas, region, config]);
  
  return null;
}

function buildSchema(schema: SchemaData, baseUrl: string, config: { phone: string; email: string }) {
  switch (schema.type) {
    case 'Organization':
      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: schema.name,
        url: schema.url || baseUrl,
        logo: schema.logo || `${baseUrl}/mtli-logo.png`,
        description: schema.description || 'One integrated platform for automation, racking, construction, electrical, installations, transitions, and lifecycle maintenance, serving North America.',
        foundingDate: schema.foundingDate || '1982',
        areaServed: schema.areaServed || ['Canada', 'United States'],
        address: schema.address ? {
          '@type': 'PostalAddress',
          addressLocality: schema.address.locality,
          addressRegion: schema.address.region,
          addressCountry: schema.address.country,
        } : undefined,
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: schema.contactPoint?.telephone || config.phone,
          contactType: schema.contactPoint?.contactType || 'sales',
        },
      };
      
    case 'LocalBusiness':
      return {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: schema.name,
        description: schema.description,
        url: baseUrl,
        telephone: schema.telephone || config.phone,
        priceRange: schema.priceRange || '$$$$',
        ...(schema.address ? { address: { '@type': 'PostalAddress', ...schema.address } } : {}),
        areaServed: {
          '@type': 'AdministrativeArea',
          name: schema.areaServed.name,
          containedInPlace: {
            '@type': 'Country',
            name: schema.areaServed.containedInPlace,
          },
        },
      };
      
    case 'Service':
      return {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: schema.name,
        description: schema.description,
        provider: {
          '@type': 'Organization',
          name: schema.provider || 'MTLI Group',
          ...(schema.providerId ? { '@id': schema.providerId } : {}),
        },
        ...(schema.url ? { url: schema.url } : {}),
        areaServed: schema.areaServed || ['Canada', 'United States'],
        serviceType: schema.serviceType,
      };
      
    case 'BreadcrumbList':
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: schema.items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`,
        })),
      };
      
    case 'FAQPage':
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: schema.questions.map(q => ({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: q.answer,
          },
        })),
      };
      
    default:
      return {};
  }
}

export default SchemaMarkup;
